import { createMD5, createSHA1 } from 'hash-wasm'
import { throwIfAborted } from './abort'

const multipartDigestCache = new WeakMap()

function blobToArrayBuffer (blob) {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer()
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsArrayBuffer(blob)
  })
}

function bytesToBase64 (bytes) {
  let binary = ''
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index])
  }
  return btoa(binary)
}

export async function getFileDigest (file, chunkSize = 2 * 1024 * 1024, signal) {
  const md5 = await createMD5()
  md5.init()

  for (let position = 0; position < file.size; position += chunkSize) {
    throwIfAborted(signal)
    const chunk = await blobToArrayBuffer(
      file.slice(position, Math.min(position + chunkSize, file.size))
    )
    md5.update(new Uint8Array(chunk))
  }
  throwIfAborted(signal)
  return md5.digest('hex')
}

export async function getMultipartDigests (file, partSize, partCount, signal) {
  const partHasher = await createMD5()
  const sha1 = await createSHA1()
  const fileKeyHasher = await createMD5()
  const partMd5HexList = []
  const partContentMd5List = []
  sha1.init()

  for (let partIndex = 0; partIndex < partCount; partIndex += 1) {
    throwIfAborted(signal)
    const start = partIndex * partSize
    const end = Math.min(start + partSize, file.size)
    const bytes = new Uint8Array(await blobToArrayBuffer(file.slice(start, end)))

    partHasher.init()
    partHasher.update(bytes)
    const partDigest = partHasher.digest('binary')
    const partMd5Hex = Array.from(partDigest)
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
    partMd5HexList.push(partMd5Hex)
    partContentMd5List.push(bytesToBase64(partDigest))
    sha1.update(bytes)
  }
  throwIfAborted(signal)

  fileKeyHasher.init()
  fileKeyHasher.update(partMd5HexList.join(''))
  return {
    fileKey: `${fileKeyHasher.digest('hex')}-${partCount}`,
    originSha1: sha1.digest('hex'),
    partMd5HexList,
    partContentMd5List
  }
}

export function getCachedMultipartDigests (file, partSize, partCount, signal) {
  const cached = multipartDigestCache.get(file)
  if (
    cached &&
    cached.partSize === partSize &&
    cached.partCount === partCount &&
    cached.signal === signal
  ) {
    return cached.promise
  }
  const promise = getMultipartDigests(file, partSize, partCount, signal)
  multipartDigestCache.set(file, { partSize, partCount, signal, promise })
  promise.catch(() => {
    const current = multipartDigestCache.get(file)
    if (current && current.promise === promise) {
      multipartDigestCache.delete(file)
    }
  })
  return promise
}
